import { defineStore } from 'pinia';
import { set, nextTick } from 'vue';
import { configRequest, getFeatureData } from 'config-request';
import { CategoryCode, SubCategoryCode, FeatureType, } from 'config-request/src/interfaces/feature-list';
// eslint-disable-next-line import/no-cycle
import { useGlobalStore } from './global';
import { useRuleCheckStore } from './rule-check';
import { getRequestIns } from '../requestIns';
import { MultipleKeyName } from '../types';
import { customStorage, isEmptyObject, isNullish } from '../utils';
import { resolveVerPageDefaultGenCode } from '../utils/version-page-info';
const snapshotData = {};
export const useFeatureListStore = defineStore('feature-list', {
    state() {
        return {
            data: null,
            // undefined: 尚未被 rule/check 覆盖（使用 feature/list 的初始值）
            // null/[]: 已被 rule/check 覆盖（不再回退 feature/list）
            runtimePackagePriceList: undefined,
            firstRequestSuccess: false,
            showCouponToast: false,
            nonSelectableOptionList: [],
        };
    },
    getters: {
        getCategoryList() {
            return this.data?.category_list ?? [];
        },
        getCarModelName() {
            return this.data?.model_name ?? '';
        },
        getOptionMap() {
            const optionMap = this.data?.option_map;
            if (!optionMap)
                return {};
            return optionMap;
        },
        // 版本页顶部提示条
        getTopTip() {
            return this.data?.top_tip;
        },
        getCarTypeChangeCouponToast() {
            return this.data?.generation_map[this.data?.generation_code]
                ?.car_type_change_coupon_toast;
        },
        /**
         *  电池容量隐藏逻辑
         */
        getBatteryVolumeHideOptions() {
            return this.data?.hide_battery_volume_option_list ?? [];
        },
        effectivePackagePriceList() {
            // 一旦发生 rule/check 覆盖（包括覆盖为空），就不再依赖 feature/list
            if (this.runtimePackagePriceList !== undefined) {
                return this.runtimePackagePriceList ?? [];
            }
            return this.data?.package_price_list ?? [];
        },
        /**
         * feature_code 到 option 的映射
         */
        featureCodeMap() {
            const optionMap = this.data?.option_map;
            if (!optionMap)
                return {};
            return Object.keys(optionMap).reduce((result, optionKey) => {
                const option = optionMap[optionKey];
                const { feature_code } = option;
                if (!feature_code)
                    return result;
                if (result[feature_code]) {
                    result[feature_code].push(option);
                }
                else {
                    result[feature_code] = [option];
                }
                return result;
            }, {});
        },
        /**
         * feature_code 到 option_list 的映射
         */
        featureCodeMapSingleOptionList() {
            if (!this.data)
                return {};
            const { category_list } = this.data;
            if (category_list?.length < 0)
                return {};
            const result = category_list.reduce((res, item) => {
                const childrenRes = item.sub_category_list.reduce((subRes, subItem) => {
                    subItem.feature_list.forEach((featureItem) => {
                        if (featureItem.feature_code &&
                            featureItem.multi_choice_type !== 1) {
                            subRes[featureItem.feature_code] = featureItem.option_list ?? [];
                        }
                    });
                    return subRes;
                }, {});
                Object.assign(res, childrenRes);
                return res;
            }, {});
            return result;
        },
        /**
         * 定制 && 选装的 feature_code 列表
         */
        optionalFeatureCodes() {
            if (!this.data) {
                return {
                    [MultipleKeyName.exteriorCustom]: [],
                    [MultipleKeyName.interiorCustom]: [],
                    [MultipleKeyName.optionalConfig]: [],
                    [MultipleKeyName.additionalConfig]: [],
                };
            }
            const exteriorCodeList = [
                CategoryCode.Exterior,
                SubCategoryCode.ExteriorCustom,
                FeatureType.ExteriorCustom,
            ];
            const exterior = getFeatureData(exteriorCodeList, this.data);
            const interiorCodeList = [
                CategoryCode.Cockpit,
                SubCategoryCode.CockpitCustom,
                FeatureType.CockpitCustom,
            ];
            const interior = getFeatureData(interiorCodeList, this.data);
            const optionalCodeList = [
                CategoryCode.Optional,
                SubCategoryCode.SubOptional,
                FeatureType.SubOptional,
            ];
            const optional = getFeatureData(optionalCodeList, this.data);
            const additionalCodeList = [
                CategoryCode.Additional,
                SubCategoryCode.SubAdditional,
                FeatureType.SubAdditional,
            ];
            const additional = getFeatureData(additionalCodeList, this.data);
            const optionMap = this.data.option_map;
            const exteriorCodes = exterior?.option_list
                ?.map((option) => optionMap[option].feature_code)
                .filter((item) => item !== undefined);
            const interiorCodes = interior?.option_list
                ?.map((option) => optionMap[option].feature_code)
                .filter((item) => item !== undefined);
            const optionalCodes = optional?.option_list
                ?.map((option) => optionMap[option].feature_code)
                .filter((item) => item !== undefined);
            const additionalCodes = additional?.option_list
                ?.map((option) => optionMap[option].feature_code)
                .filter((item) => item !== undefined);
            return {
                [MultipleKeyName.exteriorCustom]: exteriorCodes,
                [MultipleKeyName.interiorCustom]: interiorCodes,
                [MultipleKeyName.optionalConfig]: optionalCodes,
                [MultipleKeyName.additionalConfig]: additionalCodes,
            };
        },
        /**
         * 定制 && 选装的 feature_code 与 globalData 存储 key 之间的映射
         */
        featureCodeGlobalKey() {
            return Object.entries(this.optionalFeatureCodes)
                .map(([key, value]) => (value ?? []).map((v) => ({
                [v]: key,
            })))
                .flat()
                .reduce((result, current) => ({ ...result, ...current }), {});
        },
        /**
         * option code 与 分类的映射关系
         */
        optionCodeCategoryMap(state) {
            if (!state.data)
                return null;
            const categoryMap = state.data.category_list.reduce((categoryResult, current) => {
                const category = current.code;
                const subCategoryMap = current.sub_category_list.reduce((subCategoryResult, subCurrent) => {
                    const subCategory = subCurrent.code;
                    const featureMap = subCurrent.feature_list.reduce((featureResult, featureCurrent) => {
                        const optionMap = featureCurrent.option_list?.reduce((optionResult, optionCurrent) => {
                            optionResult[optionCurrent] = {
                                featureCode: featureCurrent.feature_code,
                                featureType: featureCurrent.feature_type,
                                subCategory,
                                category,
                            };
                            return optionResult;
                        }, {});
                        Object.assign(featureResult, optionMap);
                        return featureResult;
                    }, {});
                    Object.assign(subCategoryResult, featureMap);
                    return subCategoryResult;
                }, {});
                Object.assign(categoryResult, subCategoryMap);
                return categoryResult;
            }, {});
            return categoryMap;
        },
        /**
         * subcategoryCode 与分类的映射关系
         */
        subcategoryCodeCategoryMap(state) {
            const excludeCategories = [
                SubCategoryCode.CockpitCustom,
                SubCategoryCode.ExteriorCustom,
                SubCategoryCode.SubOptional,
                SubCategoryCode.SubAdditional,
            ];
            const result = {};
            const categoryList = state.data?.category_list;
            categoryList?.forEach(({ sub_category_list, code: category }) => {
                sub_category_list.forEach(({ feature_list, code }) => {
                    if (excludeCategories.includes(code))
                        return;
                    result[code] = {
                        featureCode: feature_list[0].feature_code || feature_list[0].feature_type,
                        category,
                        subCategory: code,
                        featureType: feature_list[0].feature_type,
                    };
                });
            });
            return result;
        },
    },
    actions: {
        setRuntimePackagePriceList(packagePriceList = undefined) {
            this.runtimePackagePriceList = packagePriceList;
        },
        refreshOptionDisabledStatus(nonSelectableOptionList, featureData) {
            this.nonSelectableOptionList =
                nonSelectableOptionList ?? featureData?.non_selectable_option_list ?? [];
            refreshOptionDisabledStatus(featureData ?? this.data, this.nonSelectableOptionList);
        },
        /**
         * option map 的默认选中项
         */
        _getSelectedOption(optionMap) {
            const selectedOpts = {};
            Object.values(optionMap).forEach((optionItem) => {
                if (optionItem.selected === 1 &&
                    optionItem.feature_code &&
                    optionItem.code) {
                    selectedOpts[optionItem.feature_code] = optionItem.code;
                }
            });
            return selectedOpts;
        },
        formatFeatureList(featureData) {
            featureData?.category_list.forEach((category) => {
                category.sub_category_list.forEach((subCategory) => {
                    subCategory.feature_list.forEach((featureItem) => {
                        const { multi_choice_type, option_list } = featureItem;
                        option_list?.forEach((option) => {
                            if (featureData.option_map[option]) {
                                featureData.option_map[option].multi_choice_type =
                                    multi_choice_type;
                            }
                            if (featureData.generation_map[option]) {
                                featureData.generation_map[option].multi_choice_type =
                                    multi_choice_type;
                            }
                        });
                    });
                });
            });
        },
        async queryFeatureList(params, needSelectDefault = true, headers) {
            // 如果已经有数据了,再切换版本等，报错需要弹toast
            const showToast = this.data?.model_code;
            const result = await configRequest(getRequestIns()).featureList(params, {
                throwError: true,
                showToast: showToast,
                ...headers,
            });
            // const result: any = featureResult
            this.formatFeatureList(result.data);
            const globalData = useGlobalStore();
            const ruleCheckStore = useRuleCheckStore();
            globalData.resetEnterPageList();
            if (globalData.showAllPage) {
                // 等待 this.data 更新后，再更新 non_selectable_option_list
                nextTick(() => {
                    // 配置总览页面依赖 show-all 接口返回的 non_selectable_option_list 字段
                    this.refreshOptionDisabledStatus(this.nonSelectableOptionList);
                });
            }
            else {
                // 切换版本后，重置首次访问标记
                ruleCheckStore.setIsFirstEntry(true);
                this.refreshOptionDisabledStatus(result.data?.non_selectable_option_list, result.data);
            }
            if (!needSelectDefault) {
                // 重新拉 feature/list 时重置覆盖态
                this.setRuntimePackagePriceList(undefined);
                this.data = result.data;
                return;
            }
            const { generation_code, model_code } = params;
            globalData.resetCurrentOpts({
                model_code: model_code ?? globalData.currentOptions.model_code,
                generation_code: generation_code ?? globalData.currentOptions.generation_code,
            });
            if (result.data) {
                this.data
                    ? (this.firstRequestSuccess = false)
                    : (this.firstRequestSuccess = true);
            }
            // 重新拉 feature/list 时重置覆盖态（配置总览优先保留 show-all 已写入的 package_price_list）
            if (!globalData.showAllPage) {
                this.setRuntimePackagePriceList(undefined);
            }
            /** 默认选中的版本 */
            if (!isNullish(result.data.generation_map) &&
                !isEmptyObject(result.data.generation_map)) {
                const versionCodeList = [
                    CategoryCode.VersionBattery,
                    SubCategoryCode.Version,
                    FeatureType.Version,
                ];
                const versionFeatureItem = getFeatureData(versionCodeList, result.data);
                if (versionFeatureItem) {
                    let resolvedGenerationCode = getSelectedItem(result.data.generation_map, versionFeatureItem.option_list)?.code ?? '';
                    if (result.data.version_page_info?.version_list?.length &&
                        !params.generation_code) {
                        const resolved = resolveVerPageDefaultGenCode(result.data.version_page_info, result.data.generation_map);
                        if (resolved && result.data.generation_map[resolved]) {
                            resolvedGenerationCode = resolved;
                        }
                    }
                    globalData.currentOpts.generation_code = resolvedGenerationCode;
                    result.data.generation_code = resolvedGenerationCode;
                }
            }
            else if (this.data) {
                result.data.generation_map = this.data.generation_map;
                const versionCodeList = [
                    CategoryCode.VersionBattery,
                    SubCategoryCode.Version,
                    FeatureType.Version,
                ];
                const versionFeatureItem = getFeatureData(versionCodeList, result.data);
                const legacyFeatureItem = getFeatureData(versionCodeList, this.data);
                if (versionFeatureItem && legacyFeatureItem) {
                    versionFeatureItem.option_list = legacyFeatureItem.option_list;
                }
            }
            /** 默认选中电池容量 */
            const batteryCodeList = [
                CategoryCode.VersionBattery,
                SubCategoryCode.Battery,
                FeatureType.BatteryVolume,
            ];
            const batteryFeatureItem = getFeatureData(batteryCodeList, result.data);
            if (batteryFeatureItem) {
                globalData.currentOpts.F00019 =
                    getSelectedItem(result.data.option_map, batteryFeatureItem.option_list)?.code ?? '';
            }
            /** 默认选中的电池购买方式 */
            const purchaseTypeCodeList = [
                CategoryCode.VersionBattery,
                SubCategoryCode.Battery,
                FeatureType.BatteryPurchaseType,
            ];
            const purchaseFeatureItem = getFeatureData(purchaseTypeCodeList, result.data);
            if (purchaseFeatureItem) {
                globalData.currentOpts.F00029 =
                    getSelectedItem(result.data.option_map, purchaseFeatureItem.option_list)?.code ?? '';
            }
            /** 默认选中的轮圈 */
            const wheelCodeList = [
                CategoryCode.Exterior,
                SubCategoryCode.Wheel,
                FeatureType.ExteriorWheel,
            ];
            const wheelFeatureItem = getFeatureData(wheelCodeList, result.data);
            if (wheelFeatureItem) {
                globalData.currentOpts.F00003 =
                    getSelectedItem(result.data.option_map, wheelFeatureItem.option_list)
                        ?.code ?? '';
            }
            /** 默认选中的外观颜色 */
            const colorCodeList = [
                CategoryCode.Exterior,
                SubCategoryCode.Color,
                FeatureType.ExteriorColor,
            ];
            const colorFeatureItem = getFeatureData(colorCodeList, result.data);
            if (colorFeatureItem) {
                globalData.currentOpts.F00002 =
                    getSelectedItem(result.data.option_map, colorFeatureItem.option_list)
                        ?.code ?? '';
            }
            /** 默认选中的外观选配 */
            const exteriorCodeList = [
                CategoryCode.Exterior,
                SubCategoryCode.ExteriorCustom,
            ];
            const exteriorFeatureItem = getFeatureData(exteriorCodeList, result.data);
            if (exteriorFeatureItem) {
                exteriorFeatureItem.feature_list.forEach((feature) => feature.option_list?.forEach((option) => {
                    const currentOption = result.data.option_map[option];
                    if (currentOption.selected === 1 &&
                        currentOption.feature_code &&
                        currentOption.code) {
                        globalData.currentOpts.exteriorCustom[currentOption.feature_code] = currentOption.code;
                    }
                }));
            }
            /** 默认选中的内饰颜色 */
            const interiorColorCodeList = [
                CategoryCode.Cockpit,
                SubCategoryCode.Interior,
                FeatureType.CockpitInteriorColor,
            ];
            const interiorColorFeatureItem = getFeatureData(interiorColorCodeList, result.data);
            if (interiorColorFeatureItem) {
                globalData.currentOpts.F00004 =
                    getSelectedItem(result.data.option_map, interiorColorFeatureItem.option_list)?.code ?? '';
            }
            /** 默认选中的内饰材质 */
            const interiorMaterialCodeList = [
                CategoryCode.Cockpit,
                SubCategoryCode.Interior,
                FeatureType.CockpitInteriorMaterial,
            ];
            const interiorMaterialFeatureItem = getFeatureData(interiorMaterialCodeList, result.data);
            if (interiorMaterialFeatureItem) {
                globalData.currentOpts.F00024 =
                    getSelectedItem(result.data.option_map, interiorMaterialFeatureItem.option_list)?.code ?? '';
            }
            /** 默认选中的钥匙 */
            const keyCodeList = [
                CategoryCode.Cockpit,
                SubCategoryCode.Key,
                FeatureType.CockpitKey,
            ];
            const keyFeatureItem = getFeatureData(keyCodeList, result.data);
            if (keyFeatureItem) {
                globalData.currentOpts.F00020 =
                    getSelectedItem(result.data.option_map, keyFeatureItem.option_list)
                        ?.code ?? '';
            }
            /** 默认选中的内饰选配 */
            const cockpitCodeList = [
                CategoryCode.Cockpit,
                SubCategoryCode.CockpitCustom,
            ];
            const cockpitFeatureItem = getFeatureData(cockpitCodeList, result.data);
            if (cockpitFeatureItem) {
                cockpitFeatureItem.feature_list.forEach((feature) => feature.option_list?.forEach((option) => {
                    const currentOption = result.data.option_map[option];
                    if (currentOption.selected === 1 &&
                        currentOption.feature_code &&
                        currentOption.code) {
                        globalData.currentOpts.interiorCustom[currentOption.feature_code] = currentOption.code;
                    }
                }));
            }
            /** 默认选中的选装 */
            const optionalCodeList = [
                CategoryCode.Optional,
                SubCategoryCode.SubOptional,
            ];
            const optionalFeatureItem = getFeatureData(optionalCodeList, result.data);
            if (optionalFeatureItem) {
                optionalFeatureItem.feature_list.forEach((feature) => feature.option_list?.forEach((option) => {
                    const currentOption = result.data.option_map[option];
                    if (currentOption.selected === 1 &&
                        currentOption.feature_code &&
                        currentOption.code) {
                        globalData.currentOpts.optionalConfig[currentOption.feature_code] = currentOption.code;
                    }
                }));
            }
            /** 默认选中的核心标配*/
            const additionalCodeList = [
                CategoryCode.Additional,
                SubCategoryCode.SubAdditional,
            ];
            const additionalFeatureItem = getFeatureData(additionalCodeList, result.data);
            if (additionalFeatureItem) {
                additionalFeatureItem.feature_list.forEach((feature) => feature.option_list?.forEach((option) => {
                    const currentOption = result.data.option_map[option];
                    if (currentOption.selected === 1 &&
                        currentOption.feature_code &&
                        currentOption.code) {
                        globalData.currentOpts.additionalConfig[currentOption.feature_code] = currentOption.code;
                    }
                }));
            }
            /** optionMap 中 selected 表示是默认选中的，但是这些选项可能不会在页面上做展示，所以需要给一个默认值 */
            const selectedOpt = this._getSelectedOption(result.data.option_map);
            Object.entries(selectedOpt).forEach(([key, value]) => {
                ;
                globalData.currentOpts[key] = value;
            });
            this.data = result.data;
            if (!globalData.showAllPage) {
                globalData.markEnterSubCategory(globalData.flowStatus.currentSubCategory);
            }
            return result;
            // const optionImage = useOptionImageStore()
            // optionImage.getImageList(SubCategoryCode.Version)
        },
        /**
         * 根据code判断是否选中
         * @param code
         * @returns
         */
        getCanSelected(code) {
            if (!code || !this.data)
                return {};
            const option_map_item = this.data?.option_map[code] ?? {};
            return {
                ...option_map_item,
            };
        },
        /**
         * 根据code获取具体信息
         * @param code
         * @returns
         */
        getOptionCodeItem(code) {
            if (!code || !this.data)
                return {};
            const option_map_item = this.data?.option_map[code] ?? {};
            return {
                ...option_map_item,
            };
        },
        /**
         * 根据feature_type计算出是属于第几步
         */
        featureTypeToStep(feature_type) { },
        /**
         * 判断是否是特殊类目下的选项（如核心标配等）
         * @param code
         */
        getOptionIsSpecial(code) {
            if (!code || !this.data)
                return false;
            return (this.data.option_map[code]?.feature_type === FeatureType.SubAdditional);
        },
        /** 记录当前数据 */
        snapshot() {
            snapshotData.nonSelectableOptionList = JSON.parse(JSON.stringify(this.nonSelectableOptionList));
            snapshotData.runtimePackagePriceList = JSON.parse(JSON.stringify(this.runtimePackagePriceList));
        },
        /** 回滚数据 */
        rollback() {
            if (snapshotData.nonSelectableOptionList) {
                this.refreshOptionDisabledStatus(snapshotData.nonSelectableOptionList);
            }
            this.setRuntimePackagePriceList(snapshotData.runtimePackagePriceList ?? null);
        },
    },
    persist: {
        enabled: !!window && !!window.sessionStorage,
        strategies: [
            {
                storage: customStorage,
            },
        ],
    },
});
const getSelectedItem = (optionMap, optionList) => {
    const list = optionList?.map((option) => optionMap[option]);
    return list?.find((item) => item.selected === 1);
};
const refreshOptionDisabledStatus = (featureData, nonSelectableOptionList) => {
    if (!featureData?.option_map)
        return;
    const disabledCodes = new Set(nonSelectableOptionList ?? featureData.non_selectable_option_list ?? []);
    featureData.non_selectable_option_list = Array.from(disabledCodes);
    Object.values(featureData.option_map).forEach((optionItem) => {
        const optionCode = optionItem.code;
        set(optionItem, 'disabled', !!optionCode && disabledCodes.has(optionCode));
    });
};
